import React, { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, createStyles, IconButton, makeStyles, Modal, Theme, Typography } from "@material-ui/core";
import { ConfigContext } from "context/ConfigContext";
import { TextInputTG, TGSelect } from "views/components/formElements";
import { colors } from '../../../../../utils/colors';
import { faAdd } from "@fortawesome/pro-light-svg-icons";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { AdditionalActions, CommandTypeOption } from "./CommandTypeConfiguration";
import { commandRefObjToList, convertCommandRefToString, convertStringToCommandRef, getCommandReferenceArr } from "utils/jsonPartsGenerators";
import { getAggregateIndex } from "utils/navigationUtils";
import { RadioInput } from "components/FormElements";
import { errorMsg } from "components/SnackbarUtilsConfigurator";
import { useConfirmDialog } from "context/ConfirmContext";
import FormLegend from "components/FormLegend";

interface AdditionalActionsModalProps {
  commandOption: CommandTypeOption;
  additionalActions: AdditionalActions;
  onSaveActions: (value: AdditionalActions) => void;
  isCreationCommand: boolean;
  aggregateTypeName: string;
}

const AdditionalActionsModal = ({
  commandOption,
  additionalActions,
  onSaveActions,
  isCreationCommand,
  aggregateTypeName,
}: AdditionalActionsModalProps)=> {
  const classes = useStyles();
  const { config } = useContext(ConfigContext);
  const { openConfirmation } = useConfirmDialog();
  const [isOpened, setIsOpened] = useState(false);
  const [additionalActionsDef, setAdditionalActionsDef] = useState<AdditionalActions>(additionalActions);
  const getAggregateList = () => config.aggregates.map((item) => item.typeName);

  const onCancel  = () => {
    setAdditionalActionsDef(additionalActions);
    setIsOpened(false);
  };

  const onSave = () => {
    if (additionalActionsDef.workflowConfirmationText.trim() === '') {
      return errorMsg('Confirmation Text is required');
    }
    for (let i = 0; i < additionalActionsDef.actions.length; i++) {
      const action = additionalActionsDef.actions[i];
      if (!action.label.trim()) {
        return errorMsg(`Label is required for Action ${i + 1}`);
      }
      if (!action.type) {
        return errorMsg(`Type is required for Action ${i + 1}`);
      }
      if (action.type === 'COMMAND') {
        if (!action.aggregateName?.trim()) {
          return errorMsg(`Aggregate Name is required for Action ${i + 1}`);
        }
        if (!action.action?.commandName) {
          return errorMsg(`Action is required for Action ${i + 1}`);
        }
      }
    }
    const labels = additionalActionsDef.actions.map(a => a.label.trim());
    const duplicates = labels.filter((label, i) => labels.indexOf(label) !== i);
    if (duplicates.length > 0) {
      return errorMsg(`Duplicate labels found: ${[...duplicates].join(', ')}`);
    }
    onSaveActions(additionalActionsDef);
    setIsOpened(false);
  };

  const onChangeText = (value: string) => {
    setAdditionalActionsDef(prev => ({
      ...prev!,
      workflowConfirmationText: value
    }));
  }

  const onChangeAction = (value: string, key: string, index: number) => {
    const val = key === 'action' ? convertStringToCommandRef(value) : value;
    const newActions = [...additionalActionsDef.actions];
    newActions[index] = { ...newActions[index], [key]: val };
    if (key === 'aggregateName') {
      newActions[index].action = { commandName: '', commandVersion: 0 };
    };

    setAdditionalActionsDef({ 
      ...additionalActionsDef,
      actions: newActions,
    })
  }

  const onDeleteAction = async (index: number) => {
    const resp = await openConfirmation({
      title: 'Delete Action',
      description: `Are you sure you want to delete Action ${index + 1}?`,
      confirmationText: 'Delete',
    });
    if (resp !== 'confirm') return;
    if (additionalActionsDef.actions.length <= 1) {
      return errorMsg( 'At least one action is required.');
    }
    const newActions = [...additionalActionsDef.actions];
    newActions.splice(index, 1);
    setAdditionalActionsDef({ 
      ...additionalActionsDef,
      actions: newActions,
    })
  }

  const onAddAction = () => {
    if (additionalActionsDef.actions.length >= 3) {
      return errorMsg('A maximum of 3 actions are allowed.');
    }
    const newAction = {
      label: '',
      type: 'COMMAND' as const,
      aggregateName: '',
      action: { commandName: '', commandVersion: 0},
    };
    setAdditionalActionsDef({
      ...additionalActionsDef,
      actions: [...additionalActionsDef.actions, newAction],
    });
  };
  
  const RenderAdditionalActions: React.FC = () => {  
    const [labels, setLabels] = useState(additionalActionsDef.actions.map(a => a.label));

    return (
      <>
        <Box>
          {additionalActionsDef.actions.map((item, index) => {
            const getSelectedAggregateIndex = () => getAggregateIndex(config, item.aggregateName);
            const getSelectedAggregateCommands = () =>
              commandRefObjToList(
                getCommandReferenceArr(config, (item.aggregateName !== aggregateTypeName || isCreationCommand), getSelectedAggregateIndex(), false, true)
              );

            return (
              <FormLegend 
                title={`Action ${index + 1}`}
                rightContent={
                  <IconButton
                    className={classes.trashButton}
                    component="span"
                    onClick={(e) => { 
                      onDeleteAction(index);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrashCan} className={classes.icon} />
                  </IconButton>
                }
                body={
                  <Box className={classes.group}>
                    <TextInputTG
                      id="label"
                      label="Label"
                      type={commandOption.type}
                      value={labels[index]}
                      disabled={!commandOption.selected}
                      inputStyle={commandOption.style}
                      containerStyle={classes.select}
                      onChange={value => {
                        const newLabels = [...labels];
                        newLabels[index] = value;
                        setLabels(newLabels);
                      }}      
                      onBlur={() => onChangeAction(labels[index], 'label', index)}
                    />
                    <Box>
                      <Typography style={{fontSize: 14, fontWeight: 500 }}>Type</Typography>
                      <RadioInput
                        value={item.type}
                        onChange={(value) => onChangeAction(value, 'type', index)}
                        options={[
                          { value: 'COMMAND', label: 'Command' },
                          ...(!isCreationCommand
                            ? [{ value: 'REDIRECT', label: 'Redirect to Details Page' }]
                            : []),
                          ]}
                        enableColor
                        radioFormStyle={classes.radioButton}
                      />
                    </Box>
                    {item.type === 'COMMAND' && (
                      <>
                        <TGSelect
                          id="aggregateName"
                          label="Aggregate Name"
                          options={getAggregateList()}
                          onChange={(value) => {
                            onChangeAction(value, 'aggregateName', index);
                          }}              
                          value={item?.aggregateName || ''}
                          containerStyle={classes.select}
                        />
                        <TGSelect
                          id="action"
                          label="Action"
                          value={item.action?.commandName ? convertCommandRefToString(item.action) : ''}
                          options={Object.keys(getSelectedAggregateCommands()).map((versions) => versions)}
                          onChange={(value) => {
                            onChangeAction(value, 'action', index);
                          }}
                          type="Action"
                          containerStyle={classes.select}
                        />
                      </>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Box>
      </>
    );
  };

  return (
    <>
      <Button
        onClick={() => {
          setIsOpened(true);
        }}
        className={classes.btn}
      >
        Configure Actions
      </Button>
      <Modal
        open={isOpened}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className={classes.rootContainer}
      >
        <Box className={classes.modalContainer}>
          <Box className={classes.modalHeader}>
            <Typography variant="h6" component="h2">
              Configure Additional Actions
            </Typography>
          </Box>                                                                     
          <Box className={classes.innerContainer}>
            <TextInputTG
              label="Confirmation Text"
              type={commandOption.type}
              value={additionalActionsDef.workflowConfirmationText}
              disabled={!commandOption.selected}
              inputStyle={commandOption.style}
              containerStyle={classes.select}
              onChange={(value) => {
                onChangeText(value);
              }}
            />
            <Box className={classes.additionalActionsHeader}>
              <Typography className={classes.header}>Additional Actions</Typography>
              <Button
                disableElevation
                variant="outlined"
                color="primary"
                classes={{ root: classes.addBtn }}
                className={classes.addButton}
                onClick={() => {
                  onAddAction();
                }}
                startIcon={<FontAwesomeIcon icon={faAdd} style={{ fontSize: 14 }}/>}
              >
                Add
              </Button>
            </Box>
            <RenderAdditionalActions />
          </Box>
          <Box className={classes.btnContainer}>
            <Button
              variant="outlined"
              color="primary"
              className={`${classes.button} ${classes.cancelButton}`}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={`${classes.button} ${classes.saveButton}`}
              onClick={onSave}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default AdditionalActionsModal;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rootContainer: {
      display: 'flex',
      alignItems: 'center',
      maxHeight: '100vh',
    },
    modalContainer: {
      width: 490,
      margin: 'auto',
      backgroundColor: colors.white,
      borderRadius: '5px',
      boxShadow: `0px 4px 6px 4px ${colors.black10}`,
      marginTop: 80,
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '21px 16px 20px 29px',
      height: '61px',
      borderBottom: `1px solid ${colors.black10}`,
    },
    innerContainer: {
      padding: '21px 26px',
      height: '70vh',
      overflowY: 'auto',
    },
    btn: {
      width: 150,
      height: 30,
      fontSize: '13px',
      fontWeight: 400,
      marginLeft: 35,
      background: colors.black5,
      boxShadow: `0px 1px 1px 1px ${colors.black10}`,
    },
    icon: {
      color: colors.black54,
    },
    btnContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '14px 23px 21px',
      borderTop: `1px solid ${colors.gray10}`,
      gap: '12px',
    },
    button: {
      width: 110,
      fontSize: 15,
      fontWeight: 500,
    },
    cancelButton: {
      borderWidth: 1.5,
    },
    saveButton: {
      boxShadow: 'none',
    },
    select: {
      '& .MuiFormLabel-root': {
        fontSize: 16,
        fontWeight: 500,
        color: colors.black,
      },
      paddingBottom: 20,
    },
    group: {
      padding: '20px 20px 10px 20px',
    },
    header: {
      fontSize: 16,
      fontWeight: 500,
      paddingBottom: 5,
    },
    trashButton: {
      color: colors.black54,
      padding: 10,
      fontSize: 18,
    },
    radioButton: {
      marginTop: 5,
      marginBottom: 10,
      '& .MuiFormControlLabel-label': {
        fontSize: 14,
        fontWeight: 400,
        color: colors.black,
      },
    },
    addButton: {
      backgroundColor: colors.white,
      borderRadius: 4,
      fontSize: 14,
      fontWeight: 500,
      height: 30,
      paddingLeft: 15,
    },
    addBtn: {
      height: 30,
      width: 70,
      fontSize: 14,
      fontWeight: 500,
    },
    additionalActionsHeader: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingBottom: 0,
    },
  })
);
